import { claimBrief } from '__generated__/claimBrief';
import { launchBrief } from '__generated__/launchBrief';
import { BriefState } from '__generated__/types';
import Bugsnag from 'common/utils/Bugsnag';
import { LaunchState } from 'projects/components/SquarespaceLaunchModal';
import mutations from 'projects/routes/Brief.graphql';
import { useState } from 'react';
import { useApolloClient } from 'react-apollo';

type Brief = { id: string; state: BriefState; userId: number };
type LaunchFn = (options: { delay: number }) => Promise<void>;
type ClaimFn = () => Promise<void>;

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}

function useWorkflowMutations(brief: Brief): [LaunchState | null, LaunchFn, ClaimFn] {
    const client = useApolloClient();
    const [launchState, setLaunchState] = useState<LaunchState | null>(null);

    async function claim() {
        if (brief.userId) {
            return;
        }

        await client.mutate<claimBrief>({
            mutation: mutations.claimBrief,
            variables: {
                id: brief.id,
            },
        });
    }

    /**
     * Launch brief (with optional artifical delay)
     */
    async function launch({ delay }: { delay: number }) {
        if (brief.state == BriefState.LAUNCHED) {
            return;
        }

        let launchTimeout;

        try {
            setLaunchState(LaunchState.InProgress);

            // We've had some reports of launches stalling without an error. This is currently
            // here to give us a picture of how many users are being affected by this.
            launchTimeout = setTimeout(
                () =>
                    Bugsnag.notify(
                        {
                            name: 'Launch',
                            message: 'Launch timeout',
                        },
                        { metaData: { brief: brief.id } }
                    ),
                15000
            );

            const res = await client.mutate<launchBrief>({
                mutation: mutations.launchBrief,
                variables: { id: brief.id },
            });

            if (res) {
                await sleep(delay);
                setLaunchState(LaunchState.Success);
            }
        } catch (error) {
            Bugsnag.notify(
                { name: 'Launch', message: 'Launch error' },
                { metaData: { brief: brief.id, error } }
            );
            setLaunchState(LaunchState.Error);
        } finally {
            if (launchTimeout) {
                clearTimeout(launchTimeout);
            }
        }
    }

    return [launchState, launch, claim];
}

export default useWorkflowMutations;
